/**
 * Cruscotto decisionale ricalcolo listino v2.
 *
 * Pagina multi-sezione:
 * 1. Card riepilogative (costi, margini, variazione)
 * 2. Tabella articoli con semaforo, scostamento, escludi, override
 * 3. Prodotti critici (scadenza + stock vecchio)
 * 4. Promozioni attive
 * 5. Approvazione/rifiuto batch
 *
 * Importa esclusivamente da @floraltech/ui (regola 6.8 CLAUDE.md).
 */

import {
  Button,
  Card,
  CardContent,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  Input,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@floraltech/ui';
import {
  AlertTriangle,
  ArrowDown,
  ArrowUp,
  Check,
  Clock,
  Minus,
  Package,
  RefreshCw,
  ShieldAlert,
  Tag,
  TrendingDown,
  TrendingUp,
  X,
} from 'lucide-react';
import React, { useCallback, useEffect, useState } from 'react';
import { useFormatters } from '@lib/formatters';

/* ─────────────────────────── Utility ─────────────────────────── */

const authHeaders = (): Record<string, string> => ({
  Authorization: `Bearer ${localStorage.getItem('token') ?? ''}`,
  'Content-Type': 'application/json',
});

const authGet = (): Record<string, string> => ({
  Authorization: `Bearer ${localStorage.getItem('token') ?? ''}`,
});

/* ─────────────────────────── Types ─────────────────────────── */

interface Unita {
  id: string;
  nome: string;
}

interface RicalcoloSummary {
  ricalcolo_id: string;
  totale_costi_fissi_mensili: string;
  giacenza_totale: string | null;
  num_articoli_inclusi: number | null;
  num_articoli_esclusi_imposto: number | null;
  delta_per_unita: string | null;
  margine_medio_attuale: string | null;
  margine_medio_proposto: string | null;
  variazione_media_pct: string | null;
  guadagno_stimato_attuale: string | null;
  guadagno_stimato_proposto: string | null;
  stato: string;
}

interface ListinoItem {
  id: string;
  ricalcolo_id: string | null;
  articolo_id: string;
  articolo_nome: string | null;
  articolo_sku: string | null;
  categoria: string | null;
  costo_medio_ponderato: string | null;
  costo_fisso_ripartito: string | null;
  prezzo_minimo_calcolato: string;
  prezzo_consigliato: string;
  prezzo_massimo: string;
  aliquota_iva: string | null;
  prezzo_pubblico: string | null;
  prezzo_pubblico_override: string | null;
  prezzo_precedente: string | null;
  scostamento_pct: string | null;
  escluso_manualmente: boolean;
  giacenza_al_ricalcolo: string | null;
  vendite_mese_precedente: string | null;
  semaforo: string | null;
  stato: string;
  motivazione_rifiuto: string | null;
  deciso_at: string | null;
  created_at: string;
}

interface ProdottoCritico {
  articolo_id?: string;
  lotto_id?: string;
  articolo_nome: string;
  articolo_sku: string;
  giacenza?: string;
  valore_magazzino?: string;
  giorni_alla_scadenza?: number;
  giorni_in_magazzino?: number;
  data_scadenza?: string;
}

interface Promozione {
  id: string;
  articolo_nome: string | null;
  articolo_sku: string | null;
  tipo_sconto: string;
  prezzo_originale: string | null;
  prezzo_promozionale: string;
  data_inizio: string;
  data_fine: string;
  margine_perso_stimato: string | null;
  stato: string;
}

/* ─────────────────────────── Semaforo ─────────────────────────── */

const SEMAFORO: Record<string, { icon: React.ReactNode; className: string }> = {
  verde: {
    icon: <Check className="w-3.5 h-3.5" />,
    className: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
  },
  giallo: {
    icon: <AlertTriangle className="w-3.5 h-3.5" />,
    className: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
  },
  rosso: {
    icon: <ShieldAlert className="w-3.5 h-3.5" />,
    className: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
  },
  grigio: {
    icon: <Minus className="w-3.5 h-3.5" />,
    className: 'bg-muted text-muted-foreground',
  },
};

const SemaforoBadge: React.FC<{ value: string | null }> = ({ value }) => {
  const cfg = SEMAFORO[value ?? 'grigio'] ?? SEMAFORO.grigio;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${cfg.className}`}>
      {cfg.icon}
    </span>
  );
};

/* ─────────────────────────── Componente ─────────────────────────── */

export const ListinoPrezzi: React.FC = () => {
  const fmt = useFormatters();
  const [unita, setUnita] = useState<Unita[]>([]);
  const [unitaId, setUnitaId] = useState('');
  const [ricalcolo, setRicalcolo] = useState<RicalcoloSummary | null>(null);
  const [voci, setVoci] = useState<ListinoItem[]>([]);
  const [critici, setCritici] = useState<{ in_scadenza: ProdottoCritico[]; stock_vecchio: ProdottoCritico[] }>({ in_scadenza: [], stock_vecchio: [] });
  const [promozioni, setPromozioni] = useState<Promozione[]>([]);
  const [loading, setLoading] = useState(false);
  const [filtroSemaforo, setFiltroSemaforo] = useState('');
  const [filtroCategoria, setFiltroCategoria] = useState('');
  const [rifiutoOpen, setRifiutoOpen] = useState(false);
  const [motivazione, setMotivazione] = useState('');
  const [tab, setTab] = useState<'articoli' | 'critici' | 'promozioni'>('articoli');

  /* Carica unità */
  useEffect(() => {
    fetch('/api/v1/unita', { headers: authGet() })
      .then((r) => r.json())
      .then((data: Unita[]) => {
        setUnita(data);
        if (data.length > 0) setUnitaId(data[0].id);
      })
      .catch(() => {});
  }, []);

  /* Carica listino batch corrente */
  const caricaListino = useCallback(async (ricId: string) => {
    const res = await fetch(
      `/api/v1/prezzi/listino?ricalcolo_id=${ricId}&limit=200`,
      { headers: authGet() }
    );
    if (res.ok) setVoci(await res.json());
  }, []);

  /* Carica prodotti critici */
  const caricaCritici = useCallback(async () => {
    if (!unitaId) return;
    const res = await fetch(
      `/api/v1/prezzi/listino/prodotti-critici?unita_organizzativa_id=${unitaId}`,
      { headers: authGet() }
    );
    if (res.ok) setCritici(await res.json());
  }, [unitaId]);

  /* Carica promozioni */
  const caricaPromozioni = useCallback(async () => {
    if (!unitaId) return;
    const res = await fetch(
      `/api/v1/prezzi/promozioni?unita_organizzativa_id=${unitaId}&stato=attiva`,
      { headers: authGet() }
    );
    if (res.ok) setPromozioni(await res.json());
  }, [unitaId]);

  /* Avvia ricalcolo */
  const avviaRicalcolo = async () => {
    if (!unitaId) return;
    setLoading(true);
    const now = new Date();
    const res = await fetch('/api/v1/prezzi/listino/ricalcola', {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify({
        unita_organizzativa_id: unitaId,
        anno: now.getFullYear(),
        mese: now.getMonth() + 1,
      }),
    });
    if (res.ok) {
      const data: RicalcoloSummary = await res.json();
      setRicalcolo(data);
      await caricaListino(data.ricalcolo_id);
      await caricaCritici();
      await caricaPromozioni();
    }
    setLoading(false);
  };

  /* Escludi/includi */
  const toggleEscludi = async (voce: ListinoItem) => {
    const action = voce.escluso_manualmente ? 'includi' : 'escludi';
    const res = await fetch(`/api/v1/prezzi/listino/${voce.id}/${action}`, {
      method: 'POST',
      headers: authHeaders(),
    });
    if (res.ok && ricalcolo) {
      await caricaListino(ricalcolo.ricalcolo_id);
    }
  };

  /* Approva batch */
  const approvaBatch = async () => {
    if (!ricalcolo) return;
    const res = await fetch(
      `/api/v1/prezzi/listino/approva-batch/${ricalcolo.ricalcolo_id}`,
      { method: 'POST', headers: authHeaders() }
    );
    if (res.ok) {
      setRicalcolo({ ...ricalcolo, stato: 'approvato' });
      if (ricalcolo) await caricaListino(ricalcolo.ricalcolo_id);
    }
  };

  /* Rifiuta batch */
  const rifiutaBatch = async () => {
    if (!ricalcolo || !motivazione.trim()) return;
    const res = await fetch(
      `/api/v1/prezzi/listino/rifiuta-batch/${ricalcolo.ricalcolo_id}`,
      {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ motivazione }),
      }
    );
    if (res.ok) {
      setRicalcolo({ ...ricalcolo, stato: 'rifiutato' });
      setRifiutoOpen(false);
      setMotivazione('');
    }
  };

  /* Filtri */
  const vociFiltrate = voci.filter((v) => {
    if (filtroSemaforo && v.semaforo !== filtroSemaforo) return false;
    if (filtroCategoria && v.categoria !== filtroCategoria) return false;
    return true;
  });

  const categorie = [...new Set(voci.map((v) => v.categoria).filter(Boolean))].sort();

  const isProposto = ricalcolo?.stato === 'proposto';

  return (
    <div className="p-6 space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-outfit font-semibold text-foreground">
          Ricalcolo listino
        </h1>
        <div className="flex items-center gap-3">
          <select
            value={unitaId}
            onChange={(e) => setUnitaId(e.target.value)}
            className="rounded border border-border bg-card text-foreground px-3 py-1.5 text-sm"
          >
            {unita.map((u) => (
              <option key={u.id} value={u.id}>{u.nome}</option>
            ))}
          </select>
          <Button size="sm" onClick={avviaRicalcolo} disabled={loading || !unitaId}>
            <RefreshCw className={`w-4 h-4 mr-1 ${loading ? 'animate-spin' : ''}`} />
            Ricalcola mese corrente
          </Button>
        </div>
      </div>

      {/* Card riepilogative */}
      {ricalcolo && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <Card>
            <CardContent className="p-4">
              <div className="text-xs text-muted-foreground uppercase tracking-wide">Costi fissi mensili</div>
              <div className="text-2xl font-semibold mt-1">{fmt.currency(ricalcolo.totale_costi_fissi_mensili)}</div>
              <div className="text-xs text-muted-foreground mt-1">
                Delta/unità: {fmt.currency(ricalcolo.delta_per_unita ?? '0')}
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="text-xs text-muted-foreground uppercase tracking-wide">Articoli</div>
              <div className="text-2xl font-semibold mt-1">{ricalcolo.num_articoli_inclusi ?? 0}</div>
              <div className="text-xs text-muted-foreground mt-1">
                Giacenza: {fmt.qty(ricalcolo.giacenza_totale ?? '0')} unità
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="text-xs text-muted-foreground uppercase tracking-wide">Margine</div>
              <div className="flex items-baseline gap-2 mt-1">
                <span className="text-muted-foreground text-sm">
                  {fmt.number(ricalcolo.margine_medio_attuale ?? '0', 1)}%
                </span>
                <span className="text-foreground">→</span>
                <span className="text-2xl font-semibold">
                  {fmt.number(ricalcolo.margine_medio_proposto ?? '0', 1)}%
                </span>
              </div>
              <div className="text-xs text-muted-foreground mt-1">attuale → proposto</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="text-xs text-muted-foreground uppercase tracking-wide">Variazione media</div>
              <div className="text-2xl font-semibold mt-1">
                {ricalcolo.variazione_media_pct != null
                  ? `${Number(ricalcolo.variazione_media_pct) >= 0 ? '+' : ''}${fmt.number(ricalcolo.variazione_media_pct, 2)}%`
                  : 'N/A (primo ricalcolo)'}
              </div>
              <div className="text-xs text-muted-foreground mt-1">
                rispetto al listino precedente
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Tab */}
      {ricalcolo && (
        <div className="flex gap-1 border-b border-border">
          {(['articoli', 'critici', 'promozioni'] as const).map((t) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                tab === t
                  ? 'border-accent text-foreground'
                  : 'border-transparent text-muted-foreground hover:text-foreground'
              }`}
            >
              {t === 'articoli' && `Articoli (${voci.length})`}
              {t === 'critici' && `Critici (${critici.in_scadenza.length + critici.stock_vecchio.length})`}
              {t === 'promozioni' && `Promozioni (${promozioni.length})`}
            </button>
          ))}
        </div>
      )}

      {/* Tab: Articoli */}
      {ricalcolo && tab === 'articoli' && (
        <>
          {/* Filtri */}
          <div className="flex gap-3">
            <select
              value={filtroSemaforo}
              onChange={(e) => setFiltroSemaforo(e.target.value)}
              className="rounded border border-border bg-card text-foreground px-3 py-1.5 text-sm"
            >
              <option value="">Tutti i semafori</option>
              <option value="verde">🟢 Verde</option>
              <option value="giallo">🟡 Giallo</option>
              <option value="rosso">🔴 Rosso</option>
            </select>
            <select
              value={filtroCategoria}
              onChange={(e) => setFiltroCategoria(e.target.value)}
              className="rounded border border-border bg-card text-foreground px-3 py-1.5 text-sm"
            >
              <option value="">Tutte le categorie</option>
              {categorie.map((c) => (
                <option key={c} value={c!}>{c}</option>
              ))}
            </select>
            <span className="text-sm text-muted-foreground self-center">
              {vociFiltrate.length} articoli
            </span>
          </div>

          <Card className="overflow-hidden">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-muted/50">
                    <TableHead className="w-8" />
                    <TableHead className="font-medium">Articolo</TableHead>
                    <TableHead className="font-medium">Categoria</TableHead>
                    <TableHead className="font-medium text-right">Giacenza</TableHead>
                    <TableHead className="font-medium text-right">Vendite</TableHead>
                    <TableHead className="font-medium text-right">Costo</TableHead>
                    <TableHead className="font-medium text-right">Prezzo att.</TableHead>
                    <TableHead className="font-medium text-right">Prezzo prop.</TableHead>
                    <TableHead className="font-medium text-right">Δ%</TableHead>
                    {isProposto && <TableHead className="font-medium w-16" />}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {vociFiltrate.map((v) => (
                    <TableRow
                      key={v.id}
                      className={v.escluso_manualmente ? 'opacity-40' : ''}
                    >
                      <TableCell><SemaforoBadge value={v.semaforo} /></TableCell>
                      <TableCell>
                        <div className="font-medium text-sm">{v.articolo_nome ?? '—'}</div>
                        <div className="text-xs text-muted-foreground">{v.articolo_sku}</div>
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground">
                        {v.categoria ?? '—'}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {fmt.qty(v.giacenza_al_ricalcolo ?? '0')}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {fmt.qty(v.vendite_mese_precedente ?? '0')}
                      </TableCell>
                      <TableCell className="text-right tabular-nums text-xs">
                        {fmt.currency(v.costo_medio_ponderato ?? '0')}
                        <span className="text-muted-foreground"> +{fmt.currency(v.costo_fisso_ripartito ?? '0')}</span>
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {v.prezzo_precedente ? fmt.currency(v.prezzo_precedente) : '—'}
                      </TableCell>
                      <TableCell className="text-right tabular-nums font-medium">
                        {fmt.currency(v.prezzo_pubblico_override ?? v.prezzo_pubblico ?? '0')}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {v.scostamento_pct != null ? (
                          <span className={`inline-flex items-center gap-0.5 ${
                            Number(v.scostamento_pct) > 0
                              ? 'text-red-600 dark:text-red-400'
                              : Number(v.scostamento_pct) < 0
                              ? 'text-green-600 dark:text-green-400'
                              : 'text-muted-foreground'
                          }`}>
                            {Number(v.scostamento_pct) > 0 && <ArrowUp className="w-3 h-3" />}
                            {Number(v.scostamento_pct) < 0 && <ArrowDown className="w-3 h-3" />}
                            {fmt.number(v.scostamento_pct, 1)}%
                          </span>
                        ) : (
                          <span className="text-muted-foreground">—</span>
                        )}
                      </TableCell>
                      {isProposto && (
                        <TableCell>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => toggleEscludi(v)}
                            title={v.escluso_manualmente ? 'Re-includi' : 'Escludi'}
                          >
                            {v.escluso_manualmente ? (
                              <Check className="w-4 h-4 text-green-600" />
                            ) : (
                              <X className="w-4 h-4 text-muted-foreground" />
                            )}
                          </Button>
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </Card>
        </>
      )}

      {/* Tab: Critici */}
      {ricalcolo && tab === 'critici' && (
        <div className="space-y-4">
          {critici.in_scadenza.length > 0 && (
            <Card>
              <CardContent className="p-4">
                <h3 className="font-medium text-sm flex items-center gap-2 mb-3">
                  <Clock className="w-4 h-4 text-red-500" />
                  In scadenza ({critici.in_scadenza.length})
                </h3>
                <Table>
                  <TableHeader>
                    <TableRow className="bg-muted/50">
                      <TableHead>Articolo</TableHead>
                      <TableHead className="text-right">Giacenza</TableHead>
                      <TableHead className="text-right">Scadenza</TableHead>
                      <TableHead className="text-right">Valore</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {critici.in_scadenza.map((p, i) => (
                      <TableRow key={i}>
                        <TableCell>
                          <div className="font-medium text-sm">{p.articolo_nome}</div>
                          <div className="text-xs text-muted-foreground">{p.articolo_sku}</div>
                        </TableCell>
                        <TableCell className="text-right">{fmt.qty(p.giacenza ?? '0')}</TableCell>
                        <TableCell className="text-right">
                          <span className={`font-medium ${
                            (p.giorni_alla_scadenza ?? 999) <= 30 ? 'text-red-600' : 'text-amber-600'
                          }`}>
                            {p.giorni_alla_scadenza}gg
                          </span>
                        </TableCell>
                        <TableCell className="text-right">{fmt.currency(p.valore_magazzino ?? '0')}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}

          {critici.stock_vecchio.length > 0 && (
            <Card>
              <CardContent className="p-4">
                <h3 className="font-medium text-sm flex items-center gap-2 mb-3">
                  <Package className="w-4 h-4 text-amber-500" />
                  Stock vecchio ({critici.stock_vecchio.length})
                </h3>
                <Table>
                  <TableHeader>
                    <TableRow className="bg-muted/50">
                      <TableHead>Articolo</TableHead>
                      <TableHead className="text-right">Giacenza</TableHead>
                      <TableHead className="text-right">Giorni</TableHead>
                      <TableHead className="text-right">Valore</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {critici.stock_vecchio.map((p, i) => (
                      <TableRow key={i}>
                        <TableCell>
                          <div className="font-medium text-sm">{p.articolo_nome}</div>
                          <div className="text-xs text-muted-foreground">{p.articolo_sku}</div>
                        </TableCell>
                        <TableCell className="text-right">{fmt.qty(p.giacenza ?? '0')}</TableCell>
                        <TableCell className="text-right font-medium text-amber-600">
                          {p.giorni_in_magazzino}gg
                        </TableCell>
                        <TableCell className="text-right">{fmt.currency(p.valore_magazzino ?? '0')}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}

          {critici.in_scadenza.length === 0 && critici.stock_vecchio.length === 0 && (
            <Card>
              <CardContent className="p-8 text-center text-muted-foreground">
                Nessun prodotto critico rilevato.
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* Tab: Promozioni */}
      {ricalcolo && tab === 'promozioni' && (
        <Card>
          <CardContent className="p-4">
            {promozioni.length > 0 ? (
              <Table>
                <TableHeader>
                  <TableRow className="bg-muted/50">
                    <TableHead>Articolo</TableHead>
                    <TableHead>Tipo</TableHead>
                    <TableHead className="text-right">Originale</TableHead>
                    <TableHead className="text-right">Promo</TableHead>
                    <TableHead>Periodo</TableHead>
                    <TableHead className="text-right">Margine perso</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {promozioni.map((p) => (
                    <TableRow key={p.id}>
                      <TableCell>
                        <div className="font-medium text-sm">{p.articolo_nome}</div>
                        <div className="text-xs text-muted-foreground">{p.articolo_sku}</div>
                      </TableCell>
                      <TableCell>
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-accent/10 text-accent">
                          <Tag className="w-3 h-3" />
                          {p.tipo_sconto}
                        </span>
                      </TableCell>
                      <TableCell className="text-right">{p.prezzo_originale ? fmt.currency(p.prezzo_originale) : '—'}</TableCell>
                      <TableCell className="text-right font-medium text-green-600">{fmt.currency(p.prezzo_promozionale)}</TableCell>
                      <TableCell className="text-xs">
                        {fmt.date(p.data_inizio)} → {fmt.date(p.data_fine)}
                      </TableCell>
                      <TableCell className="text-right text-red-600">
                        {p.margine_perso_stimato ? `-${fmt.currency(p.margine_perso_stimato)}` : '—'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <div className="p-8 text-center text-muted-foreground">
                Nessuna promozione attiva.
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Barra approvazione */}
      {ricalcolo && isProposto && (
        <Card className="border-accent/30">
          <CardContent className="p-4 flex items-center justify-between">
            <div className="text-sm">
              <span className="font-medium">{voci.filter((v) => !v.escluso_manualmente).length}</span>
              {' articoli da approvare · '}
              <span className="font-medium">{voci.filter((v) => v.escluso_manualmente).length}</span>
              {' esclusi'}
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setRifiutoOpen(true)}>
                <X className="w-4 h-4 mr-1" />
                Rifiuta
              </Button>
              <Button onClick={approvaBatch}>
                <Check className="w-4 h-4 mr-1" />
                Approva tutto
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Stato post-decisione */}
      {ricalcolo && ricalcolo.stato === 'approvato' && (
        <Card className="border-green-500/30">
          <CardContent className="p-4 text-center text-green-700 dark:text-green-400 font-medium">
            <Check className="w-5 h-5 inline mr-2" />
            Listino approvato
          </CardContent>
        </Card>
      )}

      {/* Stato iniziale */}
      {!ricalcolo && (
        <Card>
          <CardContent className="p-12 text-center text-muted-foreground">
            <RefreshCw className="w-8 h-8 mx-auto mb-3 opacity-30" />
            <p>Seleziona un'unità organizzativa e avvia il ricalcolo per vedere il cruscotto decisionale.</p>
          </CardContent>
        </Card>
      )}

      {/* Dialog rifiuto */}
      <Dialog open={rifiutoOpen} onOpenChange={setRifiutoOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Motivazione rifiuto</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <Input
              placeholder="Motivazione obbligatoria…"
              value={motivazione}
              onChange={(e) => setMotivazione(e.target.value)}
            />
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setRifiutoOpen(false)}>
                Annulla
              </Button>
              <Button
                variant="destructive"
                onClick={rifiutaBatch}
                disabled={!motivazione.trim()}
              >
                Conferma rifiuto
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
